/**
 * Function used to check if thean object has no content
 * @param objectName
 * @returns
 */
export const isObjectEmpty = (obj: object) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

/**
 * Function used to format the number display on a card
 * References: https://www.stylemanual.gov.au/grammar-punctuation-and-conventions/numbers-and-measurements/telephone-numbers
 * @param phoneNumber
 * @returns
 */
export const formatPhoneNumber = (phoneNumber: string) => {
  // First of all, check if the number has ten numbers (to be defined...)
  if (phoneNumber)
    return `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(
      4,
      7,
    )} ${phoneNumber.slice(7, 10)}`;
  else return "";
};

/**
 * Function used to transform the time into a clock format
 * @param time Number of seconds to transform
 * @param padMinutes Boolean to decide if padding the minutes if the number is less than 10
 * @returns
 */
export const clockify = (time: number, padMinutes: boolean = true) => {
  if (time <= 0) {
    return "00:00";
  }

  // Define the minutes and seconds
  const hours = Math.floor(time / 3600);
  const minutes =
    hours > 0 ? Math.floor(time / 60) - hours * 60 : Math.floor(time / 60);
  const seconds = time % 60;

  // Add '0' when minutes and seconds are less than 10
  // const zeroSec = seconds < 10 ? '0': '';
  // const zeroMin = minutes < 10 ? '0': '';
  const hoursStr = hours > 0 ? hours.toString() : "";
  const minutesStr = padMinutes
    ? minutes.toString().padStart(2, "0")
    : minutes.toString();
  const secondsStr = seconds.toString().padStart(2, "0");

  return `${hours > 0 ? hoursStr + ":" : ""}${minutesStr}:${secondsStr}`;
};

/**
 * Function to get the date in ISO format as a string, but
 * with the time of the current zone
 * @param date Date to transform
 * @returns
 */
export const toLocalISOTime = (date: Date) => {
  const timezoneOffsetInMinutes = date.getTimezoneOffset();
  const adjustedDate = new Date(
    date.getTime() - timezoneOffsetInMinutes * 60 * 1000,
  );
  return adjustedDate.toISOString(); // Remove the 'Z' at the end
};

/**
 * Function used to get an array of the last n days.
 * @param days Number of last days to include from today.
 * @returns
 */
export const getLastDates = (days: number) => {
  const dates = [];
  for (let i = 0; i < days; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split("T")[0]); // Format the date as YYYY-MM-DD
  }
  return dates.reverse();
};

/**
 * Function used to capitalise the first character of a string
 * @param str
 * @returns
 */
export const capitaliseFirstChar = (str: string) => {
  const firstCharCap = str.charAt(0).toUpperCase();
  const restChars = str.slice(1);
  return firstCharCap + restChars;
};

/**
 * Function used to transform a continuos string into a string
 * separated by hyphens ("-") separating spaces, underscores and
 * capital letters.
 * @param str
 * @returns
 */
export const toKebabCase = (str: string) => {
  // Get matches to check if matches are not null (for TypeScript conflicts)
  const matches = str.match(
    /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
  );
  return matches ? matches.join("-").toLowerCase() : "";
};

/**
 * Function used to allow just numbers and + character
 * when inputting numbers on an input change.
 * @param text: phone number to be entered
 * @returns text with just numbers and + character.
 */
export const allowOnlyNumbers = (text: string) => {
  return text.replace(/[^0-9\+\s]/g, "");
};

/**
 * Function utilised for redirecting to an external website.
 * @param url: string. URL to be redirected.
 */
export const externalNavigate = (url: string) => {
  window.location.href = url;
};

/**
 * Function used to remove a specific element in an array.
 * @param array
 * @param element
 * @returns
 */
export const removeItem = (array: any, element: any) => {
  return array.filter((item: any) => item !== element);
};

/**
 * Function used to check if a data URI corresponds to an image
 * @param dataURI
 * @returns boolean indicating if the provided dataURI is an image
 */
export const isImageDataURI = (dataURI: string) => {
  return typeof dataURI === "string" && dataURI.startsWith("data:image/");
};

/**
 * Function used to transform a plural work into a single one
 * @param word
 * @returns
 */
export const singlify = (word: string) => {
  if (word.endsWith("ies")) {
    return word.slice(0, -3) + "y";
  } else if (
    word.endsWith("ses") ||
    word.endsWith("xes") ||
    word.endsWith("zes")
  ) {
    return word.slice(0, -2); // e.g., "boxes" -> "box"
  } else if (word.endsWith("s") && !word.endsWith("ss")) {
    return word.slice(0, -1);
  }
  return word;
};

export const getTimestamp = (date: Date) => {
  // Defint an auxiliar padding function
  const pad = (num: number) => String(num).padStart(2, "0");

  // Transform the date into a timestamp
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // months are 0-based
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // Define the timestamp
  const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`;

  return timestamp;
};

/**
 * Function used to filter the input string to get just the
 * ASCII printable characters (between 32 and 126)
 * @param str
 */
export const sanitizeToASCIIPrintable = (str: string) => {
  return str
    .split("")
    .filter((char: string) => {
      return 32 <= char.charCodeAt(0) && char.charCodeAt(0) <= 126;
    })
    .join("");
};

/**
 * Auxiliar function used to transform the accept string into MIME
 * formats to be checked
 * @param accept
 * @returns
 */
export const acceptToMIMEFormats = (accept: string) => {
  // Define the output array
  const formats: string[] = [];

  // Get each one of the accept items
  const acceptItems = accept.split(",").map((item) => item.trim());

  // Rules per defined formats (internal logic)
  if (acceptItems.includes(".js")) {
    formats.push(
      ...[
        "text/javascript",
        "application/javascript",
        "text/x-javascript",
        "application/x-javascript",
      ],
    );
  }
  if (acceptItems.includes(".py")) {
    formats.push(...["text/x-python", "text/x-python-script"]);
  }
  if (acceptItems.includes(".sb3")) formats.push("");
  if (acceptItems.includes(".pdf")) formats.push("application/pdf");
  if (acceptItems.includes(".hex"))
    formats.push(...["application/octet-stream", "text/plain"]);
  if (acceptItems.includes(".png")) formats.push("image/png");
  if (acceptItems.includes(".jpeg")) formats.push("image/jpeg");
  if (acceptItems.includes(".gif")) formats.push("image/gif");

  return formats;
};

/**
 * Function used to hash a string
 * @param text String to be hashed.
 * @param algorithm Algorithm used to hash the string.
 * @returns
 */
export const hashBase64File = async (base64: string) => {
  // Remove "data:image/*;base64," header if present
  const cleanBase64 = base64.split(",")[1] || base64;

  // Transform into binary
  const binary = atob(cleanBase64);
  const bytes = new Uint8Array(binary.length);

  // Update the bytes array
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  // Create the hash
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Function used to clamp a current value based on a defined
 * minimum and maximum
 * @param val
 * @param min
 * @param max
 * @returns
 */
export const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

/**
 * Auxiliar function used to just update one state per time
 * @param key
 * @param setState
 * @returns
 */
export const updateStateField = (
  key: string,
  setState: React.Dispatch<React.SetStateAction<any>>,
) => {
  return (value: any) => {
    setState((prev: any) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };
};
